import "@/App.css";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useTheme } from "next-themes";
import { useEffect } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { initAudio, setSoundTheme } from "@/lib/sound";
import { PageLoader } from "@/components/Kit";
import StationLayout from "@/components/StationLayout";
import Landing from "@/pages/Landing";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AssemblyMatrix from "@/pages/AssemblyMatrix";
import Station from "@/pages/Station";
import AssemblyBay from "@/pages/AssemblyBay";
import ChargingDock from "@/pages/ChargingDock";
import CentralCore from "@/pages/CentralCore";
import GamePlay from "@/pages/GamePlay";
import Pets from "@/pages/Pets";
import RoomView from "@/pages/RoomView";
import NeonDistrict from "@/pages/NeonDistrict";
import Arena from "@/pages/Arena";
import Quests from "@/pages/Quests";
import ScrapYard from "@/pages/ScrapYard";
import Bank from "@/pages/Bank";
import Auction from "@/pages/Auction";
import Training from "@/pages/Training";
import TradingPost from "@/pages/TradingPost";
import Profile from "@/pages/Profile";
import Friends from "@/pages/Friends";
import Mail from "@/pages/Mail";
import Vault from "@/pages/Vault";
import Market from "@/pages/Market";
import FabricationDepot from "@/pages/FabricationDepot";
import Wheel from "@/pages/Wheel";
import Advent from "@/pages/Advent";
import CoreQuest from "@/pages/CoreQuest";
import SDB from "@/pages/SDB";
import NetCrawler from "@/pages/NetCrawler";
import PlayerShop from "@/pages/PlayerShop";
import Loops from "@/pages/Loops";
import Fusion from "@/pages/Fusion";
import DiscardPile from "@/pages/DiscardPile";
import FleetStatus from "@/pages/FleetStatus";
import RobotLookup from "@/pages/RobotLookup";
import MainframeNews from "@/pages/MainframeNews";

function SoundController() {
  const { resolvedTheme } = useTheme();
  useEffect(() => { if (resolvedTheme) setSoundTheme(resolvedTheme); }, [resolvedTheme]);
  useEffect(() => {
    const g = () => initAudio();
    window.addEventListener("pointerdown", g, { once: true });
    window.addEventListener("keydown", g, { once: true });
    return () => { window.removeEventListener("pointerdown", g); window.removeEventListener("keydown", g); };
  }, []);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/app/login" element={<Login />} />
      <Route path="/login" element={<Navigate to="/app/login" replace />} />
      <Route path="/app" element={<StationLayout />}>
        <Route index element={<Navigate to="/app/station" replace />} />
        <Route path="station" element={<Station />} />
        <Route path="create" element={<AssemblyMatrix />} />
        <Route path="assembly" element={<AssemblyBay />} />
        <Route path="charging" element={<ChargingDock />} />
        <Route path="arcade" element={<CentralCore />} />
        <Route path="arcade/wheel" element={<Wheel />} />
        <Route path="advent" element={<Advent />} />
        <Route path="arcade/:gameId" element={<GamePlay />} />
        <Route path="pets" element={<Pets />} />
        <Route path="shop" element={<NeonDistrict />} />
        <Route path="arena" element={<Arena />} />
        <Route path="quests" element={<Quests />} />
        <Route path="scrapyard" element={<ScrapYard />} />
        <Route path="bank" element={<Bank />} />
        <Route path="auction" element={<Auction />} />
        <Route path="training" element={<Training />} />
        <Route path="trade" element={<TradingPost />} />
        <Route path="profile" element={<Profile />} />
        <Route path="profile/:userId" element={<Profile />} />
        <Route path="friends" element={<Friends />} />
        <Route path="mail" element={<Mail />} />
        <Route path="vault" element={<Vault />} />
        <Route path="market" element={<Market />} />
        <Route path="depot" element={<FabricationDepot />} />
        <Route path="corequest" element={<CoreQuest />} />
        <Route path="storage/sdb" element={<SDB />} />
        <Route path="storage/fusion" element={<Fusion />} />
        <Route path="shops/wizard" element={<NetCrawler />} />
        <Route path="shops/user/:id" element={<PlayerShop />} />
        <Route path="loops" element={<Loops />} />
        <Route path="discard-pile" element={<DiscardPile />} />
        <Route path="fleet/status" element={<FleetStatus />} />
        <Route path="robots/lookup/:id" element={<RobotLookup />} />
        <Route path="mainframe-news" element={<MainframeNews />} />
        <Route path="room/:roomId" element={<RoomView />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <SoundController />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
